// SubPanel-Elect Analytics Service
// Business intelligence and analytics functionality

import { apiClient } from '@/services/shared/apiClient';
import type {
  BusinessIntelligence,
  RevenueAnalytics,
  CustomerAnalytics,
  MarketAnalytics,
  PerformanceAnalytics,
  BusinessForecasts,
  BusinessInsight,
  SubpanelElectApiResponse,
} from '@/types/entities/community_subpanel-elect';

export class AnalyticsService {
  /**
   * Get comprehensive business intelligence data
   */
  async getBusinessIntelligence(
    timeframe: '30d' | '90d' | '1y' = '30d',
  ): Promise<BusinessIntelligence> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<BusinessIntelligence>>(
        '/subpanel-elect/analytics/business-intelligence',
        { timeframe },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch business intelligence:', error);
      return this.getMockBusinessIntelligence();
    }
  }

  /**
   * Get revenue analytics
   */
  async getRevenueAnalytics(
    timeframe: '30d' | '90d' | '1y' = '30d',
    granularity: 'daily' | 'weekly' | 'monthly' = 'monthly',
  ): Promise<RevenueAnalytics> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<RevenueAnalytics>>(
        '/subpanel-elect/analytics/revenue',
        { timeframe, granularity },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch revenue analytics:', error);
      return this.getMockRevenueAnalytics();
    }
  }

  /**
   * Get customer analytics
   */
  async getCustomerAnalytics(timeframe: '30d' | '90d' | '1y' = '30d'): Promise<CustomerAnalytics> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<CustomerAnalytics>>(
        '/subpanel-elect/analytics/customers',
        { timeframe },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch customer analytics:', error);
      return this.getMockCustomerAnalytics();
    }
  }

  /**
   * Get market analytics
   */
  async getMarketAnalytics(): Promise<MarketAnalytics> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<MarketAnalytics>>(
        '/subpanel-elect/analytics/market',
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch market analytics:', error);
      return this.getMockMarketAnalytics();
    }
  }

  /**
   * Get performance analytics
   */
  async getPerformanceAnalytics(
    timeframe: '30d' | '90d' | '1y' = '30d',
  ): Promise<PerformanceAnalytics> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<PerformanceAnalytics>>(
        '/subpanel-elect/analytics/performance',
        { timeframe },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch performance analytics:', error);
      return this.getMockPerformanceAnalytics();
    }
  }

  /**
   * Get business forecasts
   */
  async getBusinessForecasts(horizon: '3m' | '6m' | '1y' = '6m'): Promise<BusinessForecasts> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<BusinessForecasts>>(
        '/subpanel-elect/analytics/forecasts',
        { horizon },
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch business forecasts:', error);
      return this.getMockBusinessForecasts();
    }
  }

  /**
   * Get business insights
   */
  async getBusinessInsights(filters?: {
    type?: string[];
    category?: string[];
    impact?: string[];
    acknowledged?: boolean;
    limit?: number;
  }): Promise<BusinessInsight[]> {
    try {
      const response = await apiClient.get<SubpanelElectApiResponse<BusinessInsight[]>>(
        '/subpanel-elect/analytics/insights',
        filters,
      );
      return response.data!;
    } catch (error) {
      console.error('Failed to fetch business insights:', error);
      return this.getMockBusinessInsights();
    }
  }

  /**
   * Acknowledge business insight
   */
  async acknowledgeInsight(insightId: string): Promise<void> {
    try {
      await apiClient.put(`/subpanel-elect/analytics/insights/${insightId}/acknowledge`);
    } catch (error) {
      console.error('Failed to acknowledge insight:', error);
      throw error;
    }
  }

  /**
   * Export analytics data
   */
  async exportAnalyticsData(
    type: 'revenue' | 'customers' | 'performance' | 'all',
    format: 'csv' | 'excel' | 'json' = 'excel',
    timeframe: '30d' | '90d' | '1y' = '30d',
  ): Promise<Blob> {
    try {
      const response = await fetch('/subpanel-elect/analytics/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type, format, timeframe }),
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      return await response.blob();
    } catch (error) {
      console.error('Failed to export analytics data:', error);
      throw error;
    }
  }

  // Mock data methods for development
  private getMockBusinessIntelligence(): BusinessIntelligence {
    return {
      revenue: this.getMockRevenueAnalytics(),
      customers: this.getMockCustomerAnalytics(),
      market: this.getMockMarketAnalytics(),
      performance: this.getMockPerformanceAnalytics(),
      forecasts: this.getMockBusinessForecasts(),
      insights: this.getMockBusinessInsights(),
    };
  }

  private getMockRevenueAnalytics(): RevenueAnalytics {
    return {
      current: {
        totalRevenue: 543360,
        monthlyRecurringRevenue: 45280,
        annualRecurringRevenue: 543360,
        averageRevenuePerUser: 190.85,
        averageRevenuePerTenant: 12937.14,
        grossMargin: 78.5,
        netMargin: 22.3,
        cashFlow: 121500,
        revenueGrowthRate: 12.3,
        mrrGrowthRate: 8.7,
        arrGrowthRate: 12.3,
        customerLifetimeValue: 12500,
        paybackPeriod: 6.2,
        retentionRate: 96.8,
        expansionRevenue: 8450,
      },
      historical: [
        {
          period: '2024-11',
          totalRevenue: 41850,
          subscriptionRevenue: 39200,
          oneTimeRevenue: 2650,
          refunds: 450,
          netRevenue: 41400,
          newCustomerRevenue: 4200,
          expansionRevenue: 1850,
          contractionRevenue: 650,
          churnedRevenue: 1200,
        },
      ],
      breakdown: {
        byTier: [
          {
            tier: 'basic',
            revenue: 125400,
            percentage: 23.1,
            customerCount: 18,
            averageRevenue: 6966.67,
          },
          {
            tier: 'premium',
            revenue: 298650,
            percentage: 55.0,
            customerCount: 19,
            averageRevenue: 15718.42,
          },
          {
            tier: 'enterprise',
            revenue: 119310,
            percentage: 22.0,
            customerCount: 5,
            averageRevenue: 23862.0,
          },
        ],
        byRegion: [
          { region: 'North America', revenue: 325600, percentage: 59.9, customerCount: 25 },
          { region: 'Europe', revenue: 152800, percentage: 28.1, customerCount: 12 },
          { region: 'Asia Pacific', revenue: 64960, percentage: 12.0, customerCount: 5 },
        ],
        byChannel: [
          {
            channel: 'direct',
            revenue: 380520,
            percentage: 70.0,
            customerCount: 30,
            acquisitionCost: 850,
          },
          {
            channel: 'partner',
            revenue: 108672,
            percentage: 20.0,
            customerCount: 8,
            acquisitionCost: 650,
          },
          {
            channel: 'referral',
            revenue: 54168,
            percentage: 10.0,
            customerCount: 4,
            acquisitionCost: 200,
          },
        ],
      },
      trends: [
        { period: '2024-11', value: 41850, change: 8.5, trend: 'up' },
        { period: '2024-12', value: 45280, change: 8.2, trend: 'up' },
      ],
      projections: [
        {
          period: '2025-01',
          projected: 48500,
          conservative: 46200,
          optimistic: 52000,
          confidence: 85,
          factors: ['seasonal growth', 'new feature adoption', 'market expansion'],
        },
      ],
    };
  }

  private getMockCustomerAnalytics(): CustomerAnalytics {
    return {
      acquisition: {
        totalCustomers: 42,
        newCustomers: 7,
        acquisitionRate: 4.2,
        acquisitionCost: 750,
        acquisitionChannels: [
          { channel: 'Organic Search', customers: 15, cost: 8500, conversion: 3.2 },
          { channel: 'Paid Advertising', customers: 12, cost: 15000, conversion: 2.8 },
          { channel: 'Referrals', customers: 8, cost: 2400, conversion: 8.5 },
          { channel: 'Content Marketing', customers: 7, cost: 5600, conversion: 2.1 },
        ],
        conversionFunnel: [
          { stage: 'Visitors', visitors: 15000, conversions: 750, rate: 5.0 },
          { stage: 'Leads', visitors: 750, conversions: 180, rate: 24.0 },
          { stage: 'Trials', visitors: 180, conversions: 54, rate: 30.0 },
          { stage: 'Customers', visitors: 54, conversions: 42, rate: 77.8 },
        ],
      },
      retention: {
        overallRetention: 96.8,
        churnRate: 3.2,
        retentionByTier: [
          { tier: 'basic', retention: 94.2, churn: 5.8 },
          { tier: 'premium', retention: 97.8, churn: 2.2 },
          { tier: 'enterprise', retention: 100.0, churn: 0.0 },
        ],
        cohortAnalysis: [
          { cohort: '2024-Q1', size: 12, retentionRates: [100, 91.7, 83.3, 83.3] },
          { cohort: '2024-Q2', size: 15, retentionRates: [100, 93.3, 86.7, 80.0] },
          { cohort: '2024-Q3', size: 18, retentionRates: [100, 94.4, 88.9] },
        ],
        churnReasons: [
          { reason: 'Cost concerns', count: 3, percentage: 42.9 },
          { reason: 'Feature limitations', count: 2, percentage: 28.6 },
          { reason: 'Poor support', count: 1, percentage: 14.3 },
          { reason: 'Other', count: 1, percentage: 14.3 },
        ],
      },
      satisfaction: {
        overallScore: 8.7,
        netPromoterScore: 67,
        customerSatisfactionScore: 4.3,
        customerEffortScore: 3.8,
        feedback: [
          { category: 'Product Features', score: 8.9, trend: 'improving', comments: 24 },
          { category: 'User Experience', score: 8.5, trend: 'stable', comments: 18 },
          { category: 'Customer Support', score: 9.1, trend: 'improving', comments: 15 },
          { category: 'Value for Money', score: 8.2, trend: 'stable', comments: 12 },
        ],
        supportMetrics: {
          ticketVolume: 87,
          averageResolutionTime: 4.2,
          firstContactResolution: 78.5,
          escalationRate: 8.2,
        },
      },
      segmentation: {
        segments: [
          {
            id: 'high_value',
            name: 'High Value Customers',
            description: 'Enterprise tier customers with high engagement',
            criteria: { tier: 'enterprise', engagement: '>80' },
            size: 5,
            revenue: 119310,
            characteristics: ['High engagement', 'Premium features', 'Long tenure'],
            trends: [
              { period: '2024-11', size: 4, revenue: 95800, change: 25.0 },
              { period: '2024-12', size: 5, revenue: 119310, change: 24.5 },
            ],
          },
        ],
        rfmAnalysis: {
          recency: { score: 8.2, description: 'Recent activity', distribution: [] },
          frequency: { score: 7.8, description: 'Regular usage', distribution: [] },
          monetary: { score: 8.9, description: 'High value', distribution: [] },
          segments: [
            {
              name: 'Champions',
              rfmScore: '555',
              customerCount: 8,
              revenue: 145000,
              characteristics: ['High value', 'Recent activity', 'Frequent usage'],
            },
          ],
        },
        behaviorClusters: [
          {
            id: 'power_users',
            name: 'Power Users',
            size: 12,
            characteristics: ['Daily login', 'Multiple features', 'High customization'],
            value: 18500,
            engagement: 92,
          },
        ],
      },
      lifecycle: {
        stages: [
          {
            name: 'Trial',
            description: 'Evaluating the platform',
            customerCount: 8,
            averageDuration: 14,
            averageValue: 0,
            conversionRate: 77.8,
          },
          {
            name: 'New Customer',
            description: 'Recently converted customers',
            customerCount: 12,
            averageDuration: 90,
            averageValue: 8500,
            conversionRate: 85.0,
          },
        ],
        transitions: [
          {
            from: 'Trial',
            to: 'New Customer',
            rate: 77.8,
            avgTime: 12,
            triggers: ['Feature adoption', 'Support engagement', 'Value realization'],
          },
        ],
        metrics: {
          averageLifespan: 850,
          timeToValue: 21,
          activationRate: 82.5,
          expansionRate: 18.5,
          contractionRate: 3.2,
        },
      },
    };
  }

  private getMockMarketAnalytics(): MarketAnalytics {
    return {
      competition: {
        competitors: [
          {
            name: 'CompetitorA',
            marketShare: 25.5,
            estimatedRevenue: 12500000,
            strengths: ['Strong brand', 'Large customer base'],
            weaknesses: ['Legacy technology', 'Poor UX'],
            recentMoves: ['Launched mobile app', 'Reduced pricing'],
          },
        ],
        marketShare: [
          {
            period: '2024-Q4',
            ourShare: 8.2,
            competitorShares: [
              { competitor: 'CompetitorA', share: 25.5 },
              { competitor: 'CompetitorB', share: 18.3 },
            ],
          },
        ],
        featureComparison: {
          features: [
            {
              name: 'Community Governance',
              us: true,
              competitors: [
                { name: 'CompetitorA', hasFeature: true, quality: 'good' },
                { name: 'CompetitorB', hasFeature: false, quality: 'basic' },
              ],
            },
          ],
        },
        pricingComparison: {
          tiers: [
            {
              tierName: 'Premium',
              ourPrice: 99,
              competitorPrices: [
                { competitor: 'CompetitorA', price: 129, features: ['Basic features'] },
                { competitor: 'CompetitorB', price: 89, features: ['Limited features'] },
              ],
            },
          ],
        },
      },
      opportunities: [
        {
          id: 'mobile_expansion',
          title: 'Mobile App Enhancement',
          description: 'Expand mobile capabilities to capture growing mobile user base',
          size: 250000,
          difficulty: 'medium',
          timeframe: '6 months',
          confidence: 75,
          requirements: ['Mobile development team', 'UI/UX redesign', 'Backend optimization'],
        },
      ],
      positioning: {
        currentPosition: 'Premium community management platform',
        targetPosition: 'Leading all-in-one community solution',
        differentiators: ['Advanced governance', 'Financial tools', 'High customization'],
        valueProposition:
          'Comprehensive community management with advanced governance and financial tools',
        messaging: ['Complete solution', 'Advanced features', 'Superior support'],
      },
      trends: [
        {
          name: 'AI Integration',
          description: 'Increasing demand for AI-powered community features',
          impact: 'high',
          timeline: '12-18 months',
          implications: ['Enhanced automation', 'Better insights', 'Competitive advantage'],
          actions: ['Develop AI roadmap', 'Partner with AI vendors', 'Pilot AI features'],
        },
      ],
    };
  }

  private getMockPerformanceAnalytics(): PerformanceAnalytics {
    return {
      operational: {
        efficiency: {
          resourceUtilization: 78.5,
          automationRate: 65.2,
          processEfficiency: 82.1,
          costPerTransaction: 2.45,
          timeToMarket: 45,
        },
        quality: {
          defectRate: 0.8,
          customerSatisfaction: 8.7,
          systemReliability: 99.8,
          dataAccuracy: 99.2,
          securityScore: 95.5,
        },
        scalability: {
          capacityUtilization: 68.5,
          scalabilityIndex: 8.2,
          bottlenecks: ['Database connections', 'File storage'],
          growthReadiness: 85.0,
        },
      },
      financial: {
        profitability: {
          grossMargin: 78.5,
          operatingMargin: 25.2,
          netMargin: 22.3,
          ebitda: 145000,
          returnOnInvestment: 18.5,
        },
        efficiency: {
          costStructure: [
            { category: 'Personnel', amount: 285000, percentage: 52.5 },
            { category: 'Infrastructure', amount: 125000, percentage: 23.0 },
            { category: 'Marketing', amount: 85000, percentage: 15.7 },
            { category: 'Other', amount: 48000, percentage: 8.8 },
          ],
          unitEconomics: {
            customerAcquisitionCost: 750,
            lifetimeValue: 12500,
            paybackPeriod: 6.2,
            marginPerCustomer: 9750,
          },
        },
        growth: {
          revenueGrowth: 12.3,
          customerGrowth: 8.7,
          marketGrowth: 15.2,
          productGrowth: 22.5,
        },
      },
      customer: {
        satisfaction: 8.7,
        retention: 96.8,
        engagement: 82.5,
        advocacy: 67,
      },
      product: {
        adoption: 85.2,
        usage: 78.9,
        featureUtilization: [
          { feature: 'Governance', adoption: 95.2, usage: 87.5, satisfaction: 9.1 },
          { feature: 'Finance', adoption: 78.6, usage: 65.8, satisfaction: 8.5 },
          { feature: 'Communication', adoption: 92.9, usage: 84.2, satisfaction: 8.8 },
        ],
        innovation: {
          releaseVelocity: 2.5,
          featureDelivery: 85.0,
          timeToValue: 21,
          experimentationRate: 15.5,
        },
      },
    };
  }

  private getMockBusinessForecasts(): BusinessForecasts {
    return {
      revenue: [
        {
          period: '2025-Q1',
          forecast: 145000,
          confidence: 85,
          range: { min: 135000, max: 155000 },
          assumptions: ['Current growth rate', 'No major churn', 'Seasonal factors'],
        },
      ],
      customers: [
        {
          period: '2025-Q1',
          forecast: 52,
          confidence: 80,
          range: { min: 48, max: 56 },
          assumptions: ['Maintained acquisition rate', 'Improved retention'],
        },
      ],
      market: [
        {
          period: '2025-Q1',
          forecast: 9.5,
          confidence: 70,
          range: { min: 8.8, max: 10.2 },
          assumptions: ['Market growth continues', 'Competitive landscape stable'],
        },
      ],
      scenarios: [
        {
          name: 'Optimistic Growth',
          description: 'Accelerated growth with successful product launches',
          probability: 30,
          impact: { revenue: 175000, customers: 62, market: 11.2 },
          timeline: 'Q1-Q2 2025',
          mitigations: ['Ensure scalability', 'Increase support capacity'],
        },
        {
          name: 'Economic Downturn',
          description: 'Reduced spending due to economic conditions',
          probability: 25,
          impact: { revenue: 115000, customers: 45, market: 7.8 },
          timeline: 'Q1-Q3 2025',
          mitigations: ['Focus on retention', 'Introduce budget-friendly tiers'],
        },
      ],
    };
  }

  private getMockBusinessInsights(): BusinessInsight[] {
    return [
      {
        id: '1',
        type: 'opportunity',
        category: 'revenue',
        title: 'Enterprise Tier Expansion Opportunity',
        description:
          'High conversion potential for premium customers to upgrade to enterprise tier',
        impact: 'high',
        confidence: 85,
        timeframe: '3-6 months',
        recommendations: [
          'Create targeted upgrade campaigns',
          'Develop enterprise-specific features',
          'Assign dedicated account managers',
        ],
        metrics: [
          { name: 'Potential Revenue', value: 85000, change: 0 },
          { name: 'Target Customers', value: 8, change: 0 },
          { name: 'Conversion Rate', value: 25, change: 0 },
        ],
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        acknowledged: false,
      },
      {
        id: '2',
        type: 'risk',
        category: 'customer',
        title: 'Churn Risk in Basic Tier',
        description: 'Increasing churn signals in basic tier customers',
        impact: 'medium',
        confidence: 75,
        timeframe: '1-3 months',
        recommendations: [
          'Implement proactive support outreach',
          'Enhance onboarding experience',
          'Add value-added features to basic tier',
        ],
        metrics: [
          { name: 'At-Risk Customers', value: 5, change: 15.2 },
          { name: 'Potential Revenue Loss', value: 35000, change: 0 },
          { name: 'Churn Probability', value: 65, change: 8.5 },
        ],
        createdAt: new Date(Date.now() - 172800000).toISOString(),
        acknowledged: true,
      },
    ];
  }
}

export const analyticsService = new AnalyticsService();
